import {
  Box,
  Card,
  Heading,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";

import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { AppRoute } from "@/constants/shared";
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";
import { Quest } from "@/types";

type Props = {
  quest: Quest;
  source: "homepage" | "quests-page";
};

export const QuestCardSkeleton = () => {
  return (
    <Card.Root rounded="2xl" overflow="hidden">
      <Image
        src="/assets/quests/quest-placeholder.png"
        alt="placeholder"
        filter="auto"
        blur="sm"
        aspectRatio={[250 / 100, 250 / 100, 250 / 100, 250 / 160]}
      />
      <Card.Body p={6}>
        <VStack gap={6} alignItems="flex-start">
          <VStack alignItems="flex-start" gap={3}>
            <Skeleton w="100px" h="16px" />
            <Skeleton w="200px" h="20px" />
          </VStack>

          <HStack>
            <Skeleton w="20px" h="20px" />
            <Skeleton w="100px" h="20px" />
          </HStack>

          <HStack>
            <Skeleton w="20px" h="20px" />
            <Skeleton w="100px" h="20px" />
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

export const QuestCard = ({ quest, source }: Props) => {
  const percentCompleted = (
    (quest.tasks.reduce((acc, task) => acc + (task.completed ? 1 : 0), 0) /
      quest.tasks.length) *
    100
  ).toFixed(0);

  const isAllCompleted = quest.tasks.every((task) => task.completed);

  return (
    <HoverCardWithLink
      href={quest.isSunset ? null : `${AppRoute.Quests}/${quest.id}`}
      linkProps={{
        w: "full",
        h: "full",
        onClick: () => {
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedQuest, {
            from: source,
            quest_name: quest.title,
          });
        },
      }}
      cardRootStyles={{
        w: "full",
        h: "full",
        cursor: quest.isSunset ? "not-allowed" : "pointer",
      }}
    >
      <Box
        position="relative"
        w="full"
        aspectRatio={[236 / 165, 236 / 165, 236 / 165, 250 / 160]}
        roundedTop="2xl"
        backgroundImage={`url(/assets/quests/${quest.isSunset ? "discontinued/" : ""}wave-${quest.wave}.avif)`}
        backgroundSize="cover"
        backgroundPosition="center"
        filter={quest.isUnrevealed ? "grayscale(100%)" : undefined}
      >
        {quest.isUnrevealed && (
          <Box
            position="absolute"
            top={10}
            left={10}
            right={10}
            bottom={10}
            backdropFilter="blur(10px)"
            bg="rgba(255, 255, 255, 0.20)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            rounded="2xl"
          >
            <HStack px={4} py={2} bg="white" borderRadius="full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
              >
                <g clipPath="url(#clip0_7461_6653)">
                  <path
                    d="M13 5.25H11.25V4C11.25 3.13805 10.9076 2.3114 10.2981 1.7019C9.6886 1.09241 8.86195 0.75 8 0.75C7.13805 0.75 6.3114 1.09241 5.7019 1.7019C5.09241 2.3114 4.75 3.13805 4.75 4V5.25H3C2.66848 5.25 2.35054 5.3817 2.11612 5.61612C1.8817 5.85054 1.75 6.16848 1.75 6.5V13.5C1.75 13.8315 1.8817 14.1495 2.11612 14.3839C2.35054 14.6183 2.66848 14.75 3 14.75H13C13.3315 14.75 13.6495 14.6183 13.8839 14.3839C14.1183 14.1495 14.25 13.8315 14.25 13.5V6.5C14.25 6.16848 14.1183 5.85054 13.8839 5.61612C13.6495 5.3817 13.3315 5.25 13 5.25ZM6.25 4C6.25 3.53587 6.43437 3.09075 6.76256 2.76256C7.09075 2.43437 7.53587 2.25 8 2.25C8.46413 2.25 8.90925 2.43437 9.23744 2.76256C9.56563 3.09075 9.75 3.53587 9.75 4V5.25H6.25V4ZM12.75 13.25H3.25V6.75H12.75V13.25Z"
                    fill="#747474"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_7461_6653">
                    <rect
                      width="16"
                      height="16"
                      fill="white"
                      transform="translate(0 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <Text fontSize="sm" fontWeight="500" color="text.secondary">
                Coming soon
              </Text>
            </HStack>
          </Box>
        )}
      </Box>
      <Card.Body
        p={6}
        filter={quest.isUnrevealed ? "blur(6px)" : "none"}
        pointerEvents={quest.isUnrevealed ? "none" : "auto"}
      >
        <VStack gap={6} alignItems="flex-start" h="full">
          <VStack alignItems="flex-start" gap={3}>
            <Heading fontSize="20px" fontWeight="700" lineHeight="28px">
              {quest.title}
            </Heading>
          </VStack>

          <VStack gap={6} mt="auto" alignItems="flex-start" w="full">
            <CompletionInfo
              percentCompleted={Number(percentCompleted)}
              isAllCompleted={isAllCompleted}
            />

            <HStack justifyContent="space-between" w="full">
              <HStack>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <rect width="20" height="20" rx="10" fill="#00D594" />
                  <path
                    d="M12.1429 7.94361C12.1034 7.92299 12.0753 7.89365 12.0562 7.85699L10 3.125L7.94361 7.85714C7.92467 7.89365 7.8964 7.92299 7.85699 7.94376L3.125 10L7.85714 12.0564C7.89442 12.0721 7.9239 12.1001 7.94376 12.143L10 16.875L12.0564 12.1429C12.0763 12.1001 12.1057 12.0721 12.143 12.0562L16.875 10L12.1429 7.94361ZM10 11.412C9.22022 11.412 8.58803 10.7798 8.58803 10C8.58803 9.22022 9.22007 8.58803 10 8.58803C10.7799 8.58803 11.412 9.22022 11.412 10C11.412 10.7798 10.7798 11.412 10 11.412Z"
                    fill="white"
                  />
                </svg>
                <Text fontSize="sm" fontWeight="500">
                  {quest.xpReward}
                </Text>
              </HStack>
              <UsersCompleted
                numUsersCompleted={quest.numUsersCompleted ?? 0}
              />
            </HStack>
          </VStack>
        </VStack>
      </Card.Body>
    </HoverCardWithLink>
  );
};

export const CompletionInfo = ({
  percentCompleted,
  isAllCompleted,
  size = "small",
  hideText = false,
}: {
  percentCompleted: number;
  isAllCompleted: boolean;
  size?: "large" | "small";
  hideText?: boolean;
}) => {
  return isAllCompleted ? (
    <HStack>
      <Box
        width={size === "large" ? "32px" : "24px"}
        height={size === "large" ? "32px" : "24px"}
        bg="#008AFF"
        borderRadius="50%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <g clipPath="url(#clip0_7797_23338)">
            <path
              d="M10.8469 3.79666L4.90939 9.73413C4.85768 9.78603 4.79623 9.8272 4.72857 9.8553C4.66091 9.88339 4.58837 9.89785 4.51511 9.89785C4.44185 9.89785 4.36931 9.88339 4.30165 9.8553C4.23399 9.8272 4.17254 9.78603 4.12082 9.73413L1.52318 7.13649C1.4714 7.08471 1.43033 7.02324 1.40231 6.95559C1.37429 6.88794 1.35986 6.81543 1.35986 6.74221C1.35986 6.66898 1.37429 6.59647 1.40231 6.52882C1.43033 6.46117 1.4714 6.3997 1.52318 6.34792C1.57496 6.29614 1.63643 6.25507 1.70408 6.22705C1.77173 6.19903 1.84424 6.1846 1.91747 6.1846C1.99069 6.1846 2.0632 6.19903 2.13085 6.22705C2.1985 6.25507 2.25997 6.29614 2.31175 6.34792L4.51557 8.55174L10.0592 3.00902C10.1638 2.90445 10.3056 2.8457 10.4535 2.8457C10.6014 2.8457 10.7432 2.90445 10.8478 3.00902C10.9524 3.11359 11.0111 3.25542 11.0111 3.40331C11.0111 3.55119 10.9524 3.69302 10.8478 3.79759L10.8469 3.79666Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_7797_23338">
              <rect
                width="11.8749"
                height="11.8749"
                fill="white"
                transform="translate(0.0626221 0.0625)"
              />
            </clipPath>
          </defs>
        </svg>
      </Box>
      {!hideText && (
        <Text fontSize="sm" fontWeight="500">
          Completed
        </Text>
      )}
    </HStack>
  ) : (
    <HStack>
      <Box
        position="relative"
        width={size === "large" ? "32px" : "24px"}
        height={size === "large" ? "32px" : "24px"}
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          borderRadius="50%"
          border={`2px solid #008AFF`}
        />
        <Box
          position="absolute"
          top={size === "large" ? "3px" : "2px"}
          left={size === "large" ? "3px" : "2px"}
          width={size === "large" ? "calc(100% - 6px)" : "calc(100% - 4px)"}
          height={size === "large" ? "calc(100% - 6px)" : "calc(100% - 4px)"}
          borderRadius="50%"
          border="1px solid white"
        />
        <Box
          position="absolute"
          top={size === "large" ? "4px" : "3px"}
          left={size === "large" ? "4px" : "3px"}
          width={size === "large" ? "calc(100% - 8px)" : "calc(100% - 6px)"}
          height={size === "large" ? "calc(100% - 8px)" : "calc(100% - 6px)"}
          borderRadius="50%"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            borderRadius="50%"
            css={{
              background: `conic-gradient(#008AFF ${percentCompleted * 3.6}deg, #ffffff 0deg)`,
            }}
          />
        </Box>
      </Box>
      {!hideText && (
        <Text fontSize="sm" fontWeight="500">
          {percentCompleted}%
        </Text>
      )}
    </HStack>
  );
};

export const UsersCompleted = ({
  numUsersCompleted,
}: {
  numUsersCompleted: number;
}) => {
  return (
    <HStack gap={1}>
      <Text fontSize="sm" fontWeight="500">
        {numUsersCompleted} user{numUsersCompleted === 1 ? "" : "s"}
      </Text>
      <Text color="text.secondary" fontSize="sm" fontWeight="500">
        completed this
      </Text>
    </HStack>
  );
};
